import { ForbiddenError } from '@/domain/errors/forbidden-error'
import { Account } from '@/domain/usecases'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import { DeferredData } from '@remix-run/router/dist/utils'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { defer, LoaderFunctionArgs, redirect } from 'react-router-dom'
import PathConstants from '../path-contants'

export type PromiseDeferFunction = () => Promise<DeferredData>
export type RecordsDefer = Record<string, unknown>
export type DeferFunction = PromiseDeferFunction | RecordsDefer | null
export type LoaderResponse = Promise<Response | Error | DeferredData | undefined | null>

export default abstract class BaseLoader {
  constructor(
    readonly account: Account.Model,
    readonly redirectUrl?: string,
    protected readonly queryClient?: QueryClient,
    readonly featureFlag?: FeatureFlag.Key,
    readonly ldClient?: LDClient
  ) {
    this.loader = this.loader.bind(this)
  }

  abstract loader(
    args?: LoaderFunctionArgs
  ): Promise<Error | Response | DeferredData | undefined | null> | Error | Response

  protected async decodeImportImage(importImageModule: Promise<{ default: string }>) {
    return importImageModule.then(async module => {
      const img = new Image()
      img.src = module.default
      await img.decode()
      return img
    })
  }

  protected async importRemoteModule(moduleName: string) {
    return import('../../factories/usecases').then(async module => {
      return module?.[moduleName as keyof typeof module]
    })
  }

  protected notAllowedLoader(): Error | Response {
    if (this.redirectUrl) {
      return redirect(this.redirectUrl)
    } else {
      throw new ForbiddenError()
    }
  }

  protected fallbackRedirect(): Error | Response {
    return redirect(PathConstants.Routes.HOME.value)
  }

  protected getAccount() {
    return this.account
  }

  protected deferExec(fn?: DeferFunction) {
    const allowed = !this.featureFlag || this.featureFlag.getValue(this.ldClient)
    if (allowed) {
      if (!fn) return null
      return fn instanceof Function ? (fn as PromiseDeferFunction)() : defer(fn as RecordsDefer)
    }
    return this.notAllowedLoader()
  }
}
